import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { isNullish } from "utils/utils";
import c from "lib/style/colorVar.scss";

import {
  createPaperRollRequest,
  getPaperRollDetail,
  updatePaperRollDetail,
  queryMerchantDetail,
} from "../model";
import s from "./paperRollRequestCreate.module.scss";

import Spin from "components/Spin/Spin";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Divider from "components/Divider/Divider";
import { PortalContext } from "App";

import ButtonMAS from "ButtonMAS/ButtonMAS";
import Selection from "components/Input/Selection/Selection";
import RequestStatusOptions from "utils/statusOptions/RequestStatusOptions";
import tools from "routes/tools";
import RadioInput from "components/Input/RadioInput/RadioInput";
import TextMAS from "components/TextMAS/TextMAS";
import moment from "moment";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";

const PAGE_LOCALE_KEY = "page.paperRoll.paperRollRequestCreate.";

const ERROR_INIT_STATE = {
  merchantId: false,
  merchantName: false,
  address: false,
  receiver: false,
  phone: false,
  rollCount: false,
  remarks: false,
  deliveryTrackingNumber: false,
  internalRemarks: false,
};

const INPUT_FIELD_INIT_STATE = {
  merchantId: "",
  merchantName: "",
  address: "",
  salesId: "",
  salesName: "",
  receiver: "",
  phone: "",
  rollCount: "",
  shippingPaidBy: "RECEIVER",
  status: "",
  remarks: "",
  deliveryTrackingNumber: "",
  internalRemarks: "",
  lastEmailSent: "",
};

const ADDRESS_INIT_STATE = [];

const paperRollRequestCreate = ({ path, redirectFn }) => {
  const [requestId, setRequestId] = useState(null);
  const [isCreate, setIsCreate] = useState(false);

  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const requestStatusOptions = RequestStatusOptions("all");

  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [isLoadingMerchant, setIsLoadingMerchant] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [caseFieldInput, setCaseFieldInput] = useState(INPUT_FIELD_INIT_STATE);
  const [addressOptions, setAddressOptions] = useState(ADDRESS_INIT_STATE);

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [isContinueDialogOpen, setIsContinueDialogOpen] = useState(false);

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setIsCreate(detailPageState[path].createFlag);
      setRequestId(detailPageState[path].id);
    } else if (sessionState && sessionState[path]) {
      setIsCreate(sessionState[path].createFlag);
      setRequestId(sessionState[path].id);
    } else {
      setIsCreate(true);
      setRequestId("");
      setIsLoadingDetail(false);
    }
  }, [detailPageState[path]]);

  useEffect(() => {
    if (!isCreate && requestId) {
      fetchRequestDetails(requestId);
    }
  }, [isCreate, requestId]);

  const getAddressOptions = (options = []) => {
    const groupedOptions = [];
    options
      .filter((option) => option.value)
      .forEach((option) => {
        if (groupedOptions.length === 0) {
          groupedOptions.push([option]);
        } else {
          const index = groupedOptions.findIndex((options) =>
            options.some((storedOption) => storedOption.value === option.value),
          );
          if (index !== -1) {
            groupedOptions[index].push(option);
          } else {
            groupedOptions.push([option]);
          }
        }
      });
    if (
      groupedOptions[0]?.length > 1 &&
      groupedOptions[0]?.findIndex((option) => option.hide) !== -1
    ) {
      groupedOptions[0].splice(
        groupedOptions[0].findIndex((option) => option.hide),
        1,
      );
    }
    const parsedOptions = groupedOptions.map((options, index) => {
      return {
        key: index,
        title: `${options.map((option) => option.key).join(" / ")} address: ${options[0].value
          }`,
        value: options[0].value,
      };
    });
    return parsedOptions;
  };

  const fetchRequestDetails = async (requestId) => {
    const fetchRequestMerchantDetail = async (requestAddress, merchantId) => {
      const apiResponse = await queryMerchantDetail(merchantId);
      const response = apiResponse?.data;
      if (!!response && !!Object.keys(response)?.length) {
        if (response?.message === "Unauthorized") {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        } else if (response?.responseCode !== "0000") {
          snackbar(t(PAGE_LOCALE_KEY + "fail.fetchMerchant"), "error");
        } else if (response?.responseCode === "0000") {
          const resData = response?.data;
          const address = resData.address1 ?? "";
          const officeAddress = resData.ownerAddress1 ?? "";
          const addressOptions = getAddressOptions([
            { key: "Selected", value: requestAddress, hide: true },
            { key: "Merchant", value: address },
            { key: "Office", value: officeAddress },
          ]);
          setAddressOptions(addressOptions);
          setCaseFieldInput((prev) => ({
            ...prev,
            merchantName: resData?.name,
            address: addressOptions[0]?.value,
          }));
        }
      }
    };

    setIsLoadingDetail(true);
    const apiResponse = await getPaperRollDetail(requestId);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const res = apiResponse.data;
          if (res.responseCode === "0000") {
            const resData = res?.data;
            setCaseFieldInput({
              merchantId: resData?.merchantId,
              // merchantName: resData?.merchant,
              address: resData?.address,
              receiver: resData?.receiver,
              phone: resData?.phone,
              rollCount: resData?.rollCount,
              shippingPaidBy: resData?.shippingPaidBy,
              status: resData?.status,
              remarks: resData?.remarks ?? "",
              internalRemarks: resData?.internalRemarks ?? "",
              deliveryTrackingNumber: resData?.deliveryTrackingNumber ?? "",
              lastEmailSent: resData?.lastEmailSent,
              salesName: resData?.salesName,
            });
            await fetchRequestMerchantDetail(
              resData?.address,
              resData?.merchantId,
            );
            setError(ERROR_INIT_STATE);
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fail.fetchPaperRoll"), "error");
          }
        }
      }
    }

    setIsLoadingDetail(false);
  };

  const fetchMerchantDetails = async (merchantId) => {
    if (!isValidInteger(merchantId)) {
      setError({ ...error, merchantId: true });
      return;
    }
    setIsLoadingMerchant(true);
    const apiResponse = await queryMerchantDetail(merchantId);

    const response = apiResponse?.data;

    if (!!response && !!Object.keys(response)?.length) {
      if (response?.message === "Unauthorized") {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      } else if (response?.responseCode !== "0000") {
        snackbar(t(PAGE_LOCALE_KEY + "fail.fetchMerchant"), "error");
      } else if (response?.responseCode === "0000") {
        const resData = response?.data;
        console.log("resData", resData);
        if (resData?.merchantId === null) {
          snackbar(t(PAGE_LOCALE_KEY + "fail.fetchMerchant"), "error");
        } else {
          const address = resData.address1 ?? "";
          const officeAddress = resData.ownerAddress1 ?? "";
          if (!address && !officeAddress) {
            snackbar(t(PAGE_LOCALE_KEY + "fail.merchantNoAddress"), "error");
          } else {
            const addressOptions = getAddressOptions([
              { key: "Merchant", value: address },
              { key: "Office", value: officeAddress },
            ]);
            setAddressOptions(addressOptions);
            setCaseFieldInput({
              ...caseFieldInput,
              salesName: resData?.salesName,
              salesId: resData?.salesId,
              merchantName: resData?.name,
              address: addressOptions[0]?.value,
              shippingPaidBy: resData.waiveThermalPaperFees
                ? "SENDER"
                : "RECEIVER",
            });
          }
          setError((prev) => ({
            ...prev,
            merchantName: false,
            address: false,
          }));
        }
      }
    }

    setIsLoadingMerchant(false);
  };

  // ===================== input function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setCaseFieldInput({ ...caseFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      const isValidValue = callback(caseFieldInput[field]);
      setError({ ...error, [field]: !isValidValue });
    }
    setFocus({ [field]: false });
  };

  const isNotEmpty = (txt) => {
    if (isNullish(txt)) return false;
    if (txt === "") return false;
    return true;
  };

  const isValidInteger = (value) => {
    return (
      isNotEmpty(value) &&
      Number(value) === parseInt(value) &&
      Number(value) > 0
    );
  };

  const isReceiverValid = (e) => {
    return isNotEmpty(e) && e?.length <= 30;
  };

  const isPhoneValid = (e) => {
    return isValidInteger(e) && e?.length <= 20 && e?.length >= 8;
  };

  const isRollCountValid = (e) => {
    return isValidInteger(e) && Number(e) <= 1000000 && Number(e) >= 1;
  };

  const isRemarksValid = (e) => {
    return e?.length <= 255;
  };

  const isDeliveryTrackingNumberValid = (e, { isSendEmail } = {}) => {
    return (
      ((e?.length === 0 && !isSendEmail) || e?.length === 15) &&
      !/^[\s]+$/.test(e)
    );
  };

  const validateFields = ({ isSendEmail }) => {
    const error = {
      merchantId: !isValidInteger(caseFieldInput.merchantId),
      merchantName: !isNotEmpty(caseFieldInput.merchantName),
      address: !isNotEmpty(caseFieldInput.address),
      receiver: !isReceiverValid(caseFieldInput.receiver),
      phone: !isPhoneValid(caseFieldInput.phone),
      //roll number should be non-empty and integer
      rollCount: !isRollCountValid(caseFieldInput.rollCount),
      remarks: !isRemarksValid(caseFieldInput.remarks),
      deliveryTrackingNumber: !isDeliveryTrackingNumberValid(
        caseFieldInput.deliveryTrackingNumber,
        { isSendEmail },
      ),
      internalRemarks: !isRemarksValid(caseFieldInput.internalRemarks),
    };

    setError(error);

    return !Object.values(error).some(Boolean);
  };

  // ===================== input field function end ===================== //

  const handleSubmit = async ({ isSendEmail } = {}) => {
    if (!validateFields({ isSendEmail })) return;
    setIsSubmitting(true);
    if (isCreate) {
      const payload = Object.assign(
        {},
        {
          ...caseFieldInput,
          status: "OPEN",
        },
      );
      delete payload.lastEmailSent;
      if (!payload.deliveryTrackingNumber)
        delete payload.deliveryTrackingNumber;

      const response = await createPaperRollRequest(payload);

      if (!response || !response?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "fail.createPaperRoll"), "error");
      } else {
        const responseBody = response.data;
        switch (response.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            if (responseBody.responseCode === "0000") {
              snackbar(
                t(PAGE_LOCALE_KEY + "success.createPaperRoll"),
                "success",
              );
              setCaseFieldInput(INPUT_FIELD_INIT_STATE);
              setAddressOptions(ADDRESS_INIT_STATE);
              refreshList();
              setIsContinueDialogOpen(true);
            } else {
              switch (responseBody.message) {
                case "MERCHANT_NOT_FOUND": {
                  snackbar(t(PAGE_LOCALE_KEY + "fail.merchantNotFound"), "error");
                  break;
                }
                case "MERCHANT_NOT_ACTIVE": {
                  snackbar(t(PAGE_LOCALE_KEY + "fail.merchantNotActive"), "error");
                  break;
                }
                default: {
                  snackbar(t(PAGE_LOCALE_KEY + "fail.createPaperRoll"), "error");
                  break;
                }
              }
            }
          }
        }
      }
    } else {
      const payload = Object.assign(
        {},
        {
          ...caseFieldInput,
          isSendEmail,
        },
      );
      delete payload.lastEmailSent;
      if (!payload.deliveryTrackingNumber)
        payload.deliveryTrackingNumber = null;

      const response = await updatePaperRollDetail(requestId, payload);
      if (!response || !response?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "fail.updatePaperRoll"), "error");
      } else {
        const responseBody = response.data;
        switch (response.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            if (responseBody.responseCode === "0000") {
              if (isSendEmail)
                snackbar(
                  t(PAGE_LOCALE_KEY + "success.updateSendPaperRoll"),
                  "success",
                );
              else
                snackbar(
                  t(PAGE_LOCALE_KEY + "success.updatePaperRoll"),
                  "success",
                );

              //order of execution is important, close -> redirect
              redirectFn("close", { tabId: path });
              redirectListAndRefresh();
            } else {
              switch (responseBody?.message) {
                case "SEND_EMAIL_FAILED":
                  snackbar(
                    t(PAGE_LOCALE_KEY + "fail.sendEmailFail"),
                    "warning",
                    5000,
                  );
                  break;
                default:
                  snackbar(
                    t(PAGE_LOCALE_KEY + "fail.updatePaperRoll"),
                    "error",
                  );
                  break;
              }
            }
          }
        }
      }
    }

    setIsSubmitting(false);
  };

  const refreshList = () => {
    const newpath =
      "/tools/paperRoll/paperRollRequestList/paperRollRequestList";
    redirectFn("refresh", {
      path: newpath,
    });
  };

  const redirectListAndRefresh = () => {
    const newpath =
      "/tools/paperRoll/paperRollRequestList/paperRollRequestList";

    const payload = {
      name: tools.find((tab) => tab.path === newpath).tabName,
      path: newpath,
      refresh: "refresh",
    };
    redirectFn("redirectPage", payload);
  };

  const paperRollDetailContent = () => {
    return (
      <div className={s.detailsContainer}>
        <div className={s.inputGridSmall}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.merchantId.label")}
            value={caseFieldInput.merchantId}
            onchangeFn={(e) => {
              textInputOnChange(e, "merchantId");
              setAddressOptions([]);
              setCaseFieldInput((prev) => ({
                ...prev,
                merchantName: "",
                address: "",
              }));
            }}
            onEnterDownFn={() => {
              fetchMerchantDetails(caseFieldInput.merchantId);
            }}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId", isNotEmpty)}
            sxParams={{
              textFocus: focus.merchantId,
              textError: error.merchantId,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.merchantId.errorInput")}
            disabled={!isCreate}
            mandatory
            endAttachment={
              isCreate && (
                <ButtonMAS
                  disabled={
                    isSubmitting || isLoadingMerchant || isLoadingDetail
                  }
                  onClick={() => {
                    fetchMerchantDetails(caseFieldInput.merchantId);
                  }}
                  nowrap
                >
                  {t(PAGE_LOCALE_KEY + "button.lookup")}
                </ButtonMAS>
              )
            }
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.merchantName.label")}
            value={caseFieldInput.merchantName}
            onchangeFn={(e) => textInputOnChange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName")}
            sxParams={{
              textFocus: focus.merchantName,
              textError: error.merchantName,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.merchantName.errorInput")}
            mandatory
            disabled
          />
        </div>
        <div className={s.inputGridBig}>
          <Selection
            label={t(PAGE_LOCALE_KEY + "fields.address.label")}
            selectionValue={caseFieldInput.address}
            onchangeFn={(e) => textInputOnChange(e, "address")}
            onfocusFn={() => textInputOnFocus("address")}
            onblurFn={() => textInputOnBlur("address")}
            options={addressOptions}
            sxParams={{
              selectionFocus: focus.address,
              selectionError: error.address,
            }}
            disabled={addressOptions.length === 0}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.address.errorInput")}
            mandatory
          />
        </div>
        <div className={s.inputGridSmall}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.salesName.label")}
            value={caseFieldInput.salesName}
            onchangeFn={(e) => textInputOnChange(e, "salesName")}
            onfocusFn={() => textInputOnFocus("salesName")}
            onblurFn={() => textInputOnBlur("salesName")}
            sxParams={{
              textFocus: focus.sale,
              textError: error.sale,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.salesName.errorInput")}
            disabled
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.receiver.label")}
            value={caseFieldInput.receiver}
            onchangeFn={(e) => textInputOnChange(e, "receiver")}
            onfocusFn={() => textInputOnFocus("receiver")}
            onblurFn={() => textInputOnBlur("receiver", isReceiverValid)}
            sxParams={{
              textFocus: focus.receiver,
              textError: error.receiver,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.receiver.errorInput")}
            mandatory
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.phone.label")}
            value={caseFieldInput.phone}
            onchangeFn={(e) => textInputOnChange(e, "phone")}
            onfocusFn={() => textInputOnFocus("phone")}
            onblurFn={() => textInputOnBlur("phone", isPhoneValid)}
            sxParams={{
              textFocus: focus.phone,
              textError: error.phone,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.phone.errorInput")}
            mandatory
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.rollCount.label")}
            value={caseFieldInput.rollCount}
            onchangeFn={(e) => textInputOnChange(e, "rollCount")}
            onfocusFn={() => textInputOnFocus("rollCount")}
            onblurFn={() => textInputOnBlur("rollCount", isRollCountValid)}
            sxParams={{
              textFocus: focus.rollCount,
              textError: error.rollCount,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.rollCount.errorInput")}
            mandatory
          />
        </div>
        <div className={s.inputGridBig}>
          <OutlinedText
            multiline
            label={t(PAGE_LOCALE_KEY + "fields.remarks.label")}
            value={caseFieldInput.remarks}
            onchangeFn={(e) => textInputOnChange(e, "remarks")}
            onfocusFn={() => textInputOnFocus("remarks")}
            onblurFn={() => textInputOnBlur("remarks", isRemarksValid)}
            sxParams={{ textFocus: focus.remarks, textError: error.remarks }}
            errorMessage={t(PAGE_LOCALE_KEY + "fields.remarks.errorInput")}
          />
          <RadioInput
            id="thermal-paper-fee-radio-group"
            label={t(PAGE_LOCALE_KEY + "fields.shipping.label")}
            value={caseFieldInput.shippingPaidBy}
            onChange={(e) => {
              setCaseFieldInput({
                ...caseFieldInput,
                ["shippingPaidBy"]: e.target.value,
              });
            }}
            options={[
              {
                label: t(
                  PAGE_LOCALE_KEY + "fields.shipping.options.senderPaid.label",
                ),
                value: "SENDER",
              },
              {
                label: t(
                  PAGE_LOCALE_KEY +
                  "fields.shipping.options.receiverPaid.label",
                ),
                value: "RECEIVER",
              },
            ]}
            mandatory
          />
        </div>
        <Divider />
        <TextMAS fontWeight="semibold" color="#000">
          {t(PAGE_LOCALE_KEY + "sectionTitle.operation")}
        </TextMAS>
        <div className={s.inputGridSmall}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "fields.deliveryTrackingNumber.label")}
            value={caseFieldInput.deliveryTrackingNumber}
            onchangeFn={(e) => textInputOnChange(e, "deliveryTrackingNumber")}
            onfocusFn={() => textInputOnFocus("deliveryTrackingNumber")}
            onblurFn={() =>
              textInputOnBlur(
                "deliveryTrackingNumber",
                isDeliveryTrackingNumberValid,
              )
            }
            sxParams={{
              textFocus: focus.deliveryTrackingNumber,
              textError: error.deliveryTrackingNumber,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "fields.deliveryTrackingNumber.errorInput",
            )}
          />
        </div>
        <div className={s.inputGridBig}>
          <OutlinedText
            multiline
            label={t(PAGE_LOCALE_KEY + "fields.internalRemarks.label")}
            value={caseFieldInput.internalRemarks}
            onchangeFn={(e) => textInputOnChange(e, "internalRemarks")}
            onfocusFn={() => textInputOnFocus("internalRemarks")}
            onblurFn={() => textInputOnBlur("internalRemarks", isRemarksValid)}
            sxParams={{
              textFocus: focus.internalRemarks,
              textError: error.internalRemarks,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "fields.internalRemarks.errorInput",
            )}
          />
        </div>
        {!isCreate && (
          <>
            <div className={s.inputGridSmall}>
              <Selection
                label={t(PAGE_LOCALE_KEY + "fields.status.label")}
                selectionValue={caseFieldInput.status}
                onchangeFn={(e) => textInputOnChange(e, "status")}
                onfocusFn={() => textInputOnFocus("status")}
                onblurFn={() => textInputOnBlur("status")}
                options={requestStatusOptions}
                sxParams={{ selectionFocus: focus.status }}
                mandatory
                noErrorMessage
              />
            </div>
            <div className={s.inputGridBig}>
              {
                <TextMAS color={c.blackDark999} fontSize="small">
                  {t(PAGE_LOCALE_KEY + "fields.lastEmailSent.label")}
                  {moment(caseFieldInput.lastEmailSent).isValid()
                    ? moment(caseFieldInput.lastEmailSent).format(
                      "YYYY-MM-DD HH:mm:ss",
                    )
                    : "-"}
                </TextMAS>
              }
            </div>
          </>
        )}
        <div className={s.inputItem}>
          <ButtonMAS onClick={() => handleSubmit({ isSendEmail: false })}>
            {isCreate
              ? t(PAGE_LOCALE_KEY + "button.create")
              : t(PAGE_LOCALE_KEY + "button.save")}
          </ButtonMAS>
          {!isCreate && (
            <ButtonMAS
              onClick={() => handleSubmit({ isSendEmail: true })}
              variant="secondary"
            >
              {t(PAGE_LOCALE_KEY + "button.saveSend")}
            </ButtonMAS>
          )}
        </div>
      </div>
    );
  };

  // ===================== detail function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <div className={s.detailPage}>
          {(isSubmitting || isLoadingMerchant || isLoadingDetail) && (
            <Spin type={"search"} hover />
          )}

          {isCreate ? (
            <div className={s.header}>
              <span>{t(PAGE_LOCALE_KEY + "sectionTitle.create")}</span>
            </div>
          ) : (
            <div className={s.header}>
              <span>
                {t(PAGE_LOCALE_KEY + "sectionTitle.edit") + requestId}
              </span>
            </div>
          )}
          <Divider />
          {paperRollDetailContent()}
          <SimpleDialog
            open={isContinueDialogOpen}
            dialogOncloseFn={() => setIsContinueDialogOpen(false)}
            content={t(PAGE_LOCALE_KEY + "continueDialog.title")}
            confirmFn={() => setIsContinueDialogOpen(false)}
            cancelFn={() => {
              //order of execution is important, close -> redirect
              redirectFn("close", { tabId: path });
              redirectListAndRefresh();
            }}
            positiveAction={t("component.simple.dialog.action.1")}
            negativeAction={t(PAGE_LOCALE_KEY + "continueDialog.back")}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(paperRollRequestCreate);
